import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef
} from 'react';
import { EventListData } from '../event-list';

export type EventListContextProps = {
  dialog: React.RefObject<HTMLDialogElement>;
  showDialog: () => void;
  closeDialog: () => void;
} & EventListData;

const EventListContext = createContext<EventListContextProps | undefined>(
  undefined
);

type EventListProviderProps = {
  children: ReactNode;
} & EventListData;

const EventListProvider = ({ children, ...props }: EventListProviderProps) => {
  const dialog = useRef<HTMLDialogElement>(null);

  const showDialog = () => dialog.current?.showModal();
  const closeDialog = () => dialog.current?.close();

  const closeDialogOnOutsideClick = (event: MouseEvent) => {
    if (event.target === dialog.current) {
      dialog.current?.close();
    }
  };

  useEffect(() => {
    window.addEventListener('click', closeDialogOnOutsideClick);
    return () => window.removeEventListener('click', closeDialogOnOutsideClick);
  }, []);

  return (
    <EventListContext.Provider
      value={{ dialog, showDialog, closeDialog, ...props }}
    >
      {children}
    </EventListContext.Provider>
  );
};

export const useEventListContext = () => {
  const context = useContext(EventListContext);
  if (context === undefined) {
    throw new Error('useEventListContext must be within EventListProvider');
  }

  return context;
};

export default EventListProvider;
